import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../../lib/error-copy.js'
import {
  type GoogleLinkOutcome,
  IDENTITIES_QUERY_KEY,
  completePendingGoogleLink,
  startGoogleLink,
} from '../../lib/google-link.js'
import { LOADING } from '../../lib/labels.js'
import type { LoginMethods } from '../../lib/login-identities.js'
import { useToast } from '../../lib/toast.js'
import { SettingsSectionCard } from './SettingsSectionCard.js'

const TITLE = 'Google アカウント'

type Props = { methods: LoginMethods }

export function SettingsGoogleSection({ methods }: Props) {
  const [error, setError] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  useCompletePendingGoogleLink(setError)

  const current = methods.googleAccounts[0] ?? null

  const onStart = async () => {
    setError(null)
    setIsRedirecting(true)
    try {
      await startGoogleLink(current?.identityId ?? null)
    } catch (err) {
      setIsRedirecting(false)
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    }
  }

  const description = methods.isGoogleOnly
    ? 'ログインに使っている Google アカウントです。パスワードとログイン用メールアドレスは Google 側で管理されるため、ここでは変更できません。'
    : 'Google アカウントでもログインできるようにします。'

  return (
    <SettingsSectionCard title={TITLE} description={description}>
      <div className="space-y-3">
        {methods.googleAccounts.length > 0 ? (
          <ul className="text-sm space-y-1">
            {methods.googleAccounts.map((a) => (
              <li key={a.identityId} className="break-all">
                {a.email ?? '（メールアドレス不明）'}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            紐付いている Google アカウントはありません。
          </p>
        )}
        {error && (
          <p
            role="alert"
            className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2"
          >
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {current && (
            <p className="text-xs text-muted-foreground">
              切り替えても、通知先メールアドレスは変わりません。
            </p>
          )}
          <button
            type="button"
            onClick={onStart}
            disabled={isRedirecting}
            className="ml-auto px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
          >
            {isRedirecting
              ? LOADING.fetching
              : current
                ? '別の Google アカウントに切り替える'
                : 'Google アカウントを紐付ける'}
          </button>
        </div>
      </div>
    </SettingsSectionCard>
  )
}

/** Google の画面から戻ってきたら、切り替えの後始末をして結果を知らせる */
function useCompletePendingGoogleLink(setError: (message: string) => void) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const hasRunRef = useRef(false)

  useEffect(() => {
    // 画面を開いたときに 1 回だけ。手続き中の記録は読んだ時点で消えるので、
    // StrictMode で二重に呼ばれても後始末が二重に走ることはない
    if (hasRunRef.current) return
    hasRunRef.current = true
    completePendingGoogleLink()
      .then((outcome) => {
        if (!outcome) return
        void queryClient.invalidateQueries({ queryKey: IDENTITIES_QUERY_KEY })
        const message = outcomeMessage(outcome)
        if (outcome.kind === 'error' || outcome.kind === 'unlink_failed') setError(message)
        else toast.success(message)
      })
      .catch((err: unknown) => setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK)))
  }, [queryClient, toast, setError])
}

function outcomeMessage(outcome: GoogleLinkOutcome): string {
  switch (outcome.kind) {
    case 'switched':
      return `Google アカウントを ${outcome.newEmail ?? '新しいアカウント'} に切り替えました`
    case 'linked':
      return `Google アカウント ${outcome.newEmail ?? ''} を紐付けました`
    case 'unchanged':
      return '同じ Google アカウントが選ばれたため、切り替えていません'
    case 'unlink_failed':
      return '新しい Google アカウントは紐付きましたが、これまでの紐付けを外せませんでした。少し時間を置いてから、もう一度切り替えてください。'
    case 'error':
      return outcome.message
  }
}
